// Snowvault server-sent event stream utility.
// Example SSE payload: '{"threadId":1,"response":"hello"}\n<END_OF_MESSAGE>\n{"threadId":1,"response":" world"}\n<END_OF_MESSAGE>\n'
// The EventTarget splits batched messages on the separator and dispatches each as a MessageEvent.
// JSON parsing is the consumer's responsibility.

import * as Sentry from '@sentry/react'

const SseMessageSeparator = '\n<END_OF_MESSAGE>\n'
export const SseMessageTypeStart = 'START'
export const SseMessageTypeData = 'DATA'
export const SseMessageTypeClose = 'CLOSE'
export const SseMessageTypeAbort = 'ABORT'
export const SseMessageTypeError = 'ERROR'

// extractApiErrorMessage reads a non-OK response's body and, if it matches the backend's JSON
// error shape ({"error":{"message": "..."}}, see errs.ErrResponse), returns that message. Returns
// null when the body is empty, not JSON, does not match the shape, or the message is blank, so the
// caller falls back to the bare "Request failed: <status>" line. Must never throw for a malformed
// or unreadable body: this helper is shared by every SSE-backed stream (agent, thread, proposal),
// so a parsing failure here must degrade to the fallback rather than break the error path itself.
//
// An AbortError is the one exception: it is rethrown rather than swallowed, because it means the
// caller intentionally cancelled the request (see the AbortError check in the .catch() below,
// which already exists to suppress a dispatched error for exactly that case). Swallowing it here
// instead would defeat that check and let this helper synthesize a spurious error message for a
// stream the caller deliberately tore down, rather than letting it fall through unreported like
// every other in-flight cancellation.
async function extractApiErrorMessage(response: Response): Promise<string | null> {
  let text: string
  try {
    text = await response.text()
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    return null
  }
  if (!text) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }

  if (typeof parsed !== 'object' || parsed === null || !('error' in parsed)) return null
  const errorField = parsed.error
  if (typeof errorField !== 'object' || errorField === null || !('message' in errorField)) return null
  const message = errorField.message
  if (typeof message !== 'string') return null
  const trimmed = message.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function FetchEventTarget(input: string, init: RequestInit) {
  const eventTarget = new EventTarget()
  fetch(input, init)
    .then(async (response) => {
      if (!response.ok || !response.body) {
        const fallback = `Request failed: ${response.status}`
        const message = response.ok ? fallback : ((await extractApiErrorMessage(response)) ?? fallback)
        eventTarget.dispatchEvent(new CustomEvent(SseMessageTypeError, { detail: new Error(message) }))
        return
      }
      // Built lazily, only once there is actually a body to pipe: constructing this unconditionally
      // for every call (including every non-OK response, which returns above) left a WritableStream
      // with no writer or closer ever attached to it.
      const eventStream = makeWriteableEventStream(eventTarget)
      response.body.pipeThrough(new TextDecoderStream()).pipeTo(eventStream)
    })
    .catch((error) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      Sentry.captureException(error)
      eventTarget.dispatchEvent(new CustomEvent(SseMessageTypeError, { detail: error }))
    })
  return eventTarget
}

function makeWriteableEventStream(eventTarget: EventTarget) {
  return new WritableStream({
    start() {
      eventTarget.dispatchEvent(new Event(SseMessageTypeStart))
    },
    write(message: string) {
      const messages = message.split(SseMessageSeparator)
      for (const msg of messages) {
        const data = msg.trim()
        if (data.length !== 0) {
          eventTarget.dispatchEvent(new MessageEvent<string>(SseMessageTypeData, { data }))
        }
      }
    },
    close() {
      eventTarget.dispatchEvent(new CloseEvent(SseMessageTypeClose))
    },
    abort(reason) {
      eventTarget.dispatchEvent(new CloseEvent(SseMessageTypeAbort, { reason }))
    }
  })
}
